<template>
  <div class="column no-wrap new-design">
    <template v-if="!users.length">
      <template v-if="hideTeamOptions">
        {{
          $t("share_with_users_selector.no_team_users", {
            teamName,
          })
        }}
      </template>
      <QSpinner v-else />
    </template>

    <template v-else>
      <StreamSharingSearchInput
        v-model="userFilter"
        class="sharing-input"
        :hide-team-options="hideTeamOptions"
        new-design
        :teams="teamsOptions"
      />
      <slot name="afterTeamSelect" />
      <div
        class="row scroll stretch team-container q-mt-lg"
        :class="{ team: $isDesktop, 'q-ml-sm': $isMobile }"
      >
        <div class="col q-mb-lg">
          <div class="sticky-header">
            <div class="row no-wrap q-px-sm items-center justify-between">
              <div class="col-4" />
              <div
                v-for="(accessLevel, permission) in localAccessLevels"
                :key="`all_${permission}`"
                class="row flex-1 items-center justify-end"
              >
                <Checkbox
                  class="primary"
                  :class="`${permission}-toggle-all-checkbox`"
                  :label="accessLevelTranslation(accessLevel)"
                  left-label
                  :model-value="allToggled(permission)"
                  new-design
                  @update:model-value="toggleAll(permission, $event)"
                />
              </div>
            </div>
          </div>
          <RecycleScroller
            v-slot="{ item, index }"
            :item-size="40"
            :items="filteredTeam"
            key-field="id"
            page-mode
          >
            <div
              :key="index"
              class="row no-wrap q-px-sm items-center justify-between"
              :class="{ 'bg-color-silver-whiter': index % 2 === 0 }"
            >
              <div class="col-4">
                {{ item.name }}
              </div>
              <div
                v-for="(_, permission) in localAccessLevels"
                :key="`${item.id}_${permission}_${item[permission]}`"
                class="flex flex-1 justify-end"
              >
                <Checkbox
                  class="small primary"
                  :class="`${permission}-checkbox`"
                  :model-value="item[permission]"
                  new-design
                  @update:model-value="
                    togglePermission(item, permission, $event)
                  "
                />
              </div>
            </div>
          </RecycleScroller>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { compact, keyBy, sortBy } from "lodash-es";

import { getLocaleText } from "shared/boot/i18n";
import StreamSharingSearchInput from "shared/components/streams/tabs/StreamSharingSearchInput.vue";
import useTeamPicker from "shared/composables/useTeamPicker";

const DEFAULT_ACCESS_LEVELS = {
  view: {
    label: getLocaleText("global.shared"),
    permissions: ["view"],
  },
  edit: {
    label: getLocaleText("global.edit"),
    permissions: ["view", "edit"],
  },
};

export default {
  name: "ShareWithUsersSelector",
  components: {
    StreamSharingSearchInput,
  },
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    users: {
      type: Array,
      required: true,
    },
    organisationTeams: {
      type: Array,
      required: true,
    },
    accessLevels: {
      type: Object,
      default: () => DEFAULT_ACCESS_LEVELS,
    },
    theme: {
      type: String,
      default: "default",
      validator(value) {
        return ["default", "social"].includes(value);
      },
    },
    admin: {
      type: Boolean,
    },
    hideTeamOptions: {
      type: Boolean,
    },
    teamName: {
      type: String,
      default: null,
    },
  },
  emits: ["update:modelValue"],
  setup() {
    const { organisationTeamFromPreference } = useTeamPicker();

    return { organisationTeamFromPreference };
  },
  data() {
    return {
      userFilter: "",
      shareToAll: false,
      editPermissionToAll: false,
      sortedUsers: [],
      mountedStreamUsers: [],
      defaultAccessLevels: () => ({
        view: {
          label: getLocaleText("global.shared"),
          permissions: ["view"],
        },
        edit: {
          label: getLocaleText("global.edit"),
          permissions: ["view", "edit"],
        },
      }),
    };
  },
  computed: {
    localAccessLevels() {
      return this.accessLevels || this.defaultAccessLevels;
    },
    filteredTeam() {
      const defaultPermissions = Object.keys(this.localAccessLevels).reduce(
        (obj, key) => Object.assign(obj, { [key]: false }),
        {}
      );

      return this.sortedUsers.filter(this.matchesFilter).map((user) => ({
        ...user,
        ...defaultPermissions,
        ...(this.sharedTo[String(user.id)] || {}),
      }));
    },
    modelValueLookup() {
      return keyBy(this.modelValue, "id");
    },
    sortable() {
      return Boolean(Object.keys(this.modelValueLookup).length);
    },
    sharedTo() {
      return this.users.reduce((sharedTo, user) => {
        let access;

        const existingShare = this.modelValueLookup[user.id] || {};

        const name = this.fullName(user);

        if (!existingShare.accessLevel && existingShare.removed) {
          access = {
            name,
            accessLevel: undefined,
            removed: true,
          };
        } else {
          access = existingShare.accessLevel
            ? {
                name,
                accessLevel: existingShare.accessLevel,
                ...this.localAccessLevels[
                  existingShare.accessLevel
                ].permissions.reduce(
                  (obj, key) => Object.assign(obj, { [key]: true }),
                  {}
                ),
              }
            : { name };
        }

        return Object.assign(sharedTo, { [user.id]: access });
      }, {});
    },
    teamsOptions() {
      return sortBy(this.organisationTeams.map(({ name }) => name));
    },
  },
  watch: {
    userFilter() {
      this.shareToAll = false;
      this.editPermissionToAll = false;
      this.sortUsers();
    },
    users: {
      immediate: true,
      handler: "sortUsers",
    },
    sortable: {
      handler() {
        if (this.sortable) this.sortUsers();
      },
    },
  },
  mounted() {
    this.mountedStreamUsers = JSON.parse(JSON.stringify(this.modelValue));
  },
  methods: {
    accessLevelTranslation(accessLevel) {
      const key = Object.keys(this.localAccessLevels).find(
        (accessLevelKey) =>
          this.localAccessLevels[accessLevelKey].label === accessLevel.label
      );

      return this.$t(`share_with_users_selector.access_levels.${key}`);
    },
    sortUsers() {
      this.sortedUsers = sortBy(this.users, (user) =>
        this.modelValueLookup[user.id]?.accessLevel ? 0 : 1
      );
    },
    toggleAll(accessLevel, enable) {
      const method = enable ? this.toggleOn : this.toggleOff;
      const changes = { ...this.sharedTo };

      this.filteredTeam.forEach((user) => {
        changes[user.id] = method(user, accessLevel);
      });

      this.emitChanges(changes);
    },
    allToggled(accessLevel) {
      return this.filteredTeam.every((user) => user[accessLevel]);
    },
    toggleOff(user, accessLevel) {
      const retainedAccessLevel = Object.keys(this.localAccessLevels).find(
        (level) =>
          this.localAccessLevels[level].permissions.every(
            (permission) => permission !== accessLevel
          )
      );

      return {
        id: user.id,
        accessLevel: retainedAccessLevel,
        name: user.name,
        removed: this.isUserRemoved(user, retainedAccessLevel),
      };
    },
    isUserRemoved(user, retainedAccessLevel) {
      if (
        !retainedAccessLevel &&
        this.mountedStreamUsers.some(
          (mountedStreamUser) => user.id === mountedStreamUser.id
        )
      ) {
        return true;
      }

      return false;
    },
    toggleOn(user, accessLevel) {
      if (this.sharedTo[user.id]?.[accessLevel]) {
        return {
          id: user.id,
          accessLevel: this.sharedTo[user.id].accessLevel,
          name: user.name,
        };
      }

      return {
        id: user.id,
        accessLevel,
        name: user.name,
      };
    },
    emitChanges(changes) {
      const updatedUsers = [];

      Object.entries(changes).forEach(([id, access]) => {
        updatedUsers.push({
          id,
          accessLevel: access.accessLevel,
          label: this.accessLevelLabel(access.accessLevel),
          name: access.name,
          removed: access.removed,
        });
      });

      this.$emit("update:modelValue", updatedUsers);
    },
    accessLevelLabel(access) {
      let label;

      if (access === "read_only") {
        label = this.$t("share_with_users_selector.view_only");
      } else if (access === "read_write") {
        label = this.$t("share_with_users_selector.edit");
      }

      return label;
    },
    matchesFilter(user) {
      const filter = (this.userFilter || "").toLowerCase();

      return (
        (!user.isTeam && this.fullName(user).toLowerCase().includes(filter)) ||
        this.teamNamesForUser(user).some((team) =>
          team.toLowerCase().includes(filter)
        )
      );
    },
    teamNamesForUser(user) {
      if (this.hideTeamOptions) return [];

      if (user.teamName || user.secondaryTeamNames) {
        return compact([...[user.teamName], ...user.secondaryTeamNames]);
      }

      return user.organisation_teams.map((team) => team.name);
    },
    fullName(user) {
      if (user.name) {
        return user.name;
      }

      return `${user.first_name} ${user.last_name}`;
    },
    togglePermission(selectedUser, accessLevel, enable) {
      let newPermissions;
      const changes = { ...this.sharedTo };

      if (enable) {
        newPermissions = this.toggleOn(selectedUser, accessLevel);
      } else {
        newPermissions = this.toggleOff(selectedUser, accessLevel);
      }

      changes[selectedUser.id] = newPermissions;
      this.emitChanges(changes);
    },
  },
};
</script>

<style lang="scss" scoped>
.team-container {
  min-height: 100px;
  height: calc(100vh - 500px);
}

:deep(.team) {
  border: 1px solid $ds-denim-2;
}

.team-users {
  background-color: $body-background;
  border-top: 1px solid $hover-background;
}

:deep(.new-design),
:deep(.new-design > input),
:deep(.new-design > a) {
  background-color: white;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: white;
  border-bottom: 1px solid $ds-denim-2;
  margin-bottom: -1px;
}
</style>
